import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
    Clock,
    AlertTriangle,
    Receipt,
    Scale,
    ArrowRight,
    TrendingUp,
    CheckCircle2,
    Target,
    Calendar,
    Sparkles,
    AlertCircle,
    Bell,
    ChevronRight,
    PoundSterling,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useHomepageMetrics } from "@/hooks/useHomepageMetrics";

export function Homepage() {
    // Get metrics from centralized data store
    const { todaysPriorities, todaysWins, keyMetrics, weekAhead } = useHomepageMetrics();

    // Static data that doesn't need to match detail screens
    const riskAlerts = [
        {
            id: 1,
            type: "credit_score",
            customer: "Global Industries Ltd",
            customerId: "3",
            message: "Credit score decreased to 67",
            severity: "high",
            link: "/customers/3",
        },
        {
            id: 2,
            type: "broken_promise",
            customer: "Acme Corporation",
            customerId: "1",
            message: "Broken promise - £25,000",
            severity: "critical",
            link: "/collections/promises?customer=1",
        },
        {
            id: 3,
            type: "aging",
            customer: "Tech Solutions Inc",
            customerId: "2",
            message: "Invoice aging 90+ days - £89,000",
            severity: "high",
            link: "/collections/ageing-analysis?customer=2",
        },
    ];

    const notifications = [
        { id: 1, message: "3 new receipts requiring allocation", time: "10 mins ago", link: "/payments/receipts?filter=unallocated" },
        { id: 2, message: "1 dispute response received", time: "25 mins ago", link: "/disputes?filter=active" },
        { id: 3, message: "2 cases escalated to you", time: "1 hour ago", link: "/collections/queue?filter=escalated" },
    ];

    const aiRecommendation = {
        customer: "Manufacturing Co Ltd",
        customerId: "5",
        probability: 89,
        expectedRecovery: 505000,
        reason: "High payment probability based on historical patterns",
        link: "/customers/5",
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case "critical":
                return "bg-red-500/10 text-red-700 border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-800";
            case "high":
                return "bg-orange-500/10 text-orange-700 border-orange-200 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-800";
            default:
                return "bg-yellow-500/10 text-yellow-700 border-yellow-200 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-800";
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header Section */}
            <div>
                <p className="text-muted-foreground">
                    {new Date().toLocaleDateString("en-GB", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </p>
            </div>

            {/* Section 1: Today's Priorities */}
            <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Today's Priorities
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {/* Promises Due Today */}
                    <Link to="/collections/promises?filter=due-today">
                        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500">
                            <CardHeader className="pb-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                        <CardTitle className="text-lg">Promises Due</CardTitle>
                                    </div>
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                        {todaysPriorities.promisesDueToday.count}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-2">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {formatCurrency(todaysPriorities.promisesDueToday.value)}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Requires follow-up today
                                </p>
                            </CardContent>
                        </Card>
                    </Link>

                    {/* Cases Due Today */}
                    <Link to="/collections/queue?filter=action-due-today">
                        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-orange-500">
                            <CardHeader className="pb-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                                        <CardTitle className="text-lg">Cases Due</CardTitle>
                                    </div>
                                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                                        {todaysPriorities.casesDueToday.count}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-2">
                                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                    {formatCurrency(todaysPriorities.casesDueToday.value)}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Action required today
                                </p>
                            </CardContent>
                        </Card>
                    </Link>

                    {/* Unallocated Receipts */}
                    <Link to="/payments/receipts?filter=unallocated">
                        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-500">
                            <CardHeader className="pb-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <PoundSterling className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                        <CardTitle className="text-lg">Unallocated</CardTitle>
                                    </div>
                                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                        {todaysPriorities.unallocatedReceipts.count}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-2">
                                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                    {formatCurrency(todaysPriorities.unallocatedReceipts.value)}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Receipts pending allocation
                                </p>
                            </CardContent>
                        </Card>
                    </Link>

                    {/* Disputes Requiring Response */}
                    <Link to="/disputes?filter=active">
                        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-red-500">
                            <CardHeader className="pb-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Scale className="h-5 w-5 text-red-600 dark:text-red-400" />
                                        <CardTitle className="text-lg">Disputes</CardTitle>
                                    </div>
                                    <Badge variant="secondary" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                        {todaysPriorities.disputesRequiringResponse.count}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-2">
                                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                    {formatCurrency(todaysPriorities.disputesRequiringResponse.value)}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Requiring response
                                </p>
                            </CardContent>
                        </Card>
                    </Link>
                </div>

                {/* Quick Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                    <Link to="/collections/queue">
                        <Button className="gap-2">
                            <Target className="h-4 w-4" />
                            Start Collections Queue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                    <Link to="/payments/receipts?filter=unallocated">
                        <Button variant="outline" className="gap-2">
                            <Receipt className="h-4 w-4" />
                            Allocate Receipts
                        </Button>
                    </Link>
                    <Link to="/collections/promises?filter=due-this-week">
                        <Button variant="outline" className="gap-2">
                            <Clock className="h-4 w-4" />
                            Review Promises
                        </Button>
                    </Link>
                    <Link to="/disputes?filter=active">
                        <Button variant="outline" className="gap-2">
                            <Scale className="h-4 w-4" />
                            Check Disputes
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Section 2: Performance Snapshot & Risk Alerts */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Performance Snapshot */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                            Performance Snapshot
                        </CardTitle>
                        <CardDescription>Your achievements today</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Today's Wins */}
                        <div className="space-y-3">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                Today's Wins
                            </h3>
                            <div className="space-y-2 pl-6">
                                <Link to="/payments/receipts?filter=today" className="block">
                                    <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                                        <span className="text-muted-foreground">Payments Received</span>
                                        <span className="font-semibold flex items-center gap-1">
                                            {todaysWins.paymentsReceived.count} • {formatCurrency(todaysWins.paymentsReceived.value)}
                                            <ChevronRight className="h-3 w-3" />
                                        </span>
                                    </div>
                                </Link>
                                <Link to="/collections/promises?filter=kept-today" className="block">
                                    <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                                        <span className="text-muted-foreground">Promises Kept</span>
                                        <span className="font-semibold flex items-center gap-1">
                                            {todaysWins.promisesKept.count}
                                            <ChevronRight className="h-3 w-3" />
                                        </span>
                                    </div>
                                </Link>
                                <Link to="/collections/queue?filter=resolved-today" className="block">
                                    <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                                        <span className="text-muted-foreground">Cases Resolved</span>
                                        <span className="font-semibold flex items-center gap-1">
                                            {todaysWins.casesResolved.count}
                                            <ChevronRight className="h-3 w-3" />
                                        </span>
                                    </div>
                                </Link>
                            </div>
                        </div>

                        {/* Collection Rate */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">Collection Rate</span>
                                <span className="font-semibold text-green-600 dark:text-green-400">
                                    {todaysWins.collectionRate}%
                                </span>
                            </div>
                            <Progress value={todaysWins.collectionRate} className="h-2" />
                            <p className="text-xs text-muted-foreground">
                                Target: {todaysWins.target}% • +{todaysWins.collectionRate - todaysWins.target}% above target
                            </p>
                        </div>

                        {/* Key Metrics */}
                        <div className="pt-3 border-t space-y-2">
                            <h3 className="font-semibold text-sm">Key Metrics</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1 p-2 rounded-lg bg-accent/30">
                                    <p className="text-xs text-muted-foreground">DSO</p>
                                    <p className="text-lg font-bold">
                                        {keyMetrics.dso.current}
                                        <span className="text-xs font-normal text-muted-foreground ml-1">
                                            days
                                        </span>
                                    </p>
                                    <p className="text-xs text-orange-600 dark:text-orange-400">
                                        Target: {keyMetrics.dso.target}
                                    </p>
                                </div>
                                <div className="space-y-1 p-2 rounded-lg bg-accent/30">
                                    <p className="text-xs text-muted-foreground">CEI</p>
                                    <p className="text-lg font-bold">
                                        {keyMetrics.cei.current}%
                                    </p>
                                    <p className="text-xs text-green-600 dark:text-green-400">
                                        ↑ {keyMetrics.cei.current - keyMetrics.cei.lastWeek}% vs last week
                                    </p>
                                </div>
                                <Link to="/collections/promises" className="block">
                                    <div className="space-y-1 p-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                                        <p className="text-xs text-muted-foreground">Promise Success</p>
                                        <p className="text-lg font-bold">{keyMetrics.promiseSuccessRate}%</p>
                                    </div>
                                </Link>
                                <Link to="/disputes" className="block">
                                    <div className="space-y-1 p-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                                        <p className="text-xs text-muted-foreground">Dispute Resolution</p>
                                        <p className="text-lg font-bold">
                                            {keyMetrics.avgDisputeResolutionDays}
                                            <span className="text-xs font-normal text-muted-foreground ml-1">
                                                days
                                            </span>
                                        </p>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Risk Alerts & Notifications */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                            Risk Alerts
                        </CardTitle>
                        <CardDescription>High-priority items requiring attention</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Risk Alerts */}
                        <div className="space-y-2">
                            {riskAlerts.map((alert) => (
                                <Link key={alert.id} to={alert.link}>
                                    <div
                                        className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)} hover:shadow-md transition-shadow cursor-pointer`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1">
                                                <p className="font-semibold text-sm">{alert.customer}</p>
                                                <p className="text-xs mt-1">{alert.message}</p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 flex-shrink-0" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* Notifications */}
                        <div className="pt-3 border-t space-y-2">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                                <Bell className="h-4 w-4" />
                                Notifications
                            </h3>
                            <div className="space-y-2">
                                {notifications.map((notification) => (
                                    <Link key={notification.id} to={notification.link}>
                                        <div className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                                            <div className="flex-1">
                                                <p className="text-sm">{notification.message}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {notification.time}
                                                </p>
                                            </div>
                                            <ChevronRight className="h-3 w-3 flex-shrink-0 mt-1" />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Section 3: Week Ahead & AI Insights */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Week Ahead */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            Week Ahead
                        </CardTitle>
                        <CardDescription>Upcoming promises and scheduled actions</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {weekAhead.map((day, index) => (
                                <Link key={index} to={`/collections/promises?filter=due-date&date=${day.date}`}>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-accent/50 hover:bg-accent transition-colors cursor-pointer">
                                        <div className="flex items-center gap-3">
                                            <div className="font-semibold text-sm w-12">{day.day}</div>
                                            <div className="text-sm text-muted-foreground">
                                                {day.promises} {day.promises === 1 ? "promise" : "promises"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="font-semibold text-sm">
                                                {formatCurrency(day.value)}
                                            </div>
                                            <ChevronRight className="h-4 w-4" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* AI Insights */}
                <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900/30">
                                    <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <CardTitle className="text-purple-900 dark:text-purple-100">
                                    AI Insights
                                </CardTitle>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300">
                                {aiRecommendation.probability}% confidence
                            </Badge>
                        </div>
                        <CardDescription className="text-purple-700 dark:text-purple-300">
                            Machine learning recommendations
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Top Priority Recommendation */}
                        <div className="rounded-lg bg-white/80 dark:bg-gray-900/80 p-4 border border-purple-200 dark:border-purple-800">
                            <Link to={aiRecommendation.link}>
                                <div className="flex items-start gap-3 mb-3 hover:opacity-80 transition-opacity cursor-pointer">
                                    <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900/30">
                                        <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-sm text-purple-900 dark:text-purple-100 mb-1">
                                            Top Priority Collection
                                        </h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 flex items-center gap-1">
                                            {aiRecommendation.customer}
                                            <ChevronRight className="h-3 w-3" />
                                        </p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                            {aiRecommendation.reason}
                                        </p>
                                    </div>
                                </div>
                            </Link>
                            <div className="space-y-2">
                                <Link to={`/collections/queue?customer=${aiRecommendation.customerId}`}>
                                    <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-700 dark:hover:bg-purple-800">
                                        Initiate Collection Call
                                    </Button>
                                </Link>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs text-purple-700 dark:text-purple-300">
                                        <span>Expected Recovery</span>
                                        <span className="font-semibold">
                                            {formatCurrency(aiRecommendation.expectedRecovery)}
                                        </span>
                                    </div>
                                    <Progress
                                        value={aiRecommendation.probability}
                                        className="h-2 bg-purple-200 dark:bg-purple-900/30 [&>div]:bg-purple-600 dark:[&>div]:bg-purple-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Additional Insights */}
                        <div className="rounded-lg bg-white/80 dark:bg-gray-900/80 p-4 border border-purple-200 dark:border-purple-800">
                            <div className="flex items-start gap-3">
                                <div className="rounded-full p-2 bg-blue-100 dark:bg-blue-900/30">
                                    <Target className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-sm text-purple-900 dark:text-purple-100 mb-1">
                                        Optimization Tip
                                    </h4>
                                    <p className="text-sm text-purple-700 dark:text-purple-300">
                                        Focus on customers with 60-90 day aging for best recovery rates this week
                                    </p>
                                    <Link to="/collections/ageing-analysis">
                                        <Button
                                            variant="link"
                                            className="h-auto p-0 text-xs text-purple-600 dark:text-purple-400 mt-1"
                                        >
                                            View aging analysis →
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
